import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, ExternalLink } from "lucide-react";
import { LOGOS, HERO_IMAGES } from "@/lib/brand";

export default function BranchPage({ branch }) {
  const b = branch;
  return (
    <div data-testid={`page-branch-${b.key}`}>
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES[b.key]})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/85 to-[#0a0a0a]" />
        <div
          className="absolute inset-x-0 bottom-0 h-72 blur-3xl opacity-60"
          style={{ background: `radial-gradient(closest-side, ${b.accent}55, transparent)` }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12" style={{ backgroundColor: b.accent }} />
              <span className="text-xs uppercase tracking-[0.3em]" style={{ color: b.accent }}>
                {b.short}
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter leading-[0.95]">
              {b.name}
            </h1>
            <p className="mt-6 text-xl text-zinc-300 max-w-2xl">{b.tagline}</p>
            <p className="mt-6 text-zinc-400 max-w-2xl leading-relaxed">{b.description}</p>
            {b.key === "proprete" && (
              <a
                href="https://equinoxeproprete.fr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="proprete-external-link"
                className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md font-semibold transition-all border"
                style={{
                  backgroundColor: "transparent",
                  color: b.accent,
                  borderColor: b.accent,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = b.accent;
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = b.accent;
                }}
              >
                Visiter le site dédié d'Equinoxe Propreté <ExternalLink size={16} />
              </a>
            )}
            {b.key === "studio" && (
              <a
                href="https://equinoxe.studio/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studio-external-link"
                className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md font-semibold transition-all border"
                style={{
                  backgroundColor: "transparent",
                  color: b.accent,
                  borderColor: b.accent,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = b.accent;
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = b.accent;
                }}
              >
                Visiter le site dédié d'Equinoxe Studio <ExternalLink size={16} />
              </a>
            )}
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div
              className={`rounded-2xl overflow-hidden bg-white shadow-2xl shadow-black/40 ring-1 ring-white/10 ${b.glow} flex items-center justify-center`}
              style={{ width: 320, height: 320 }}
            >
              <img
                src={LOGOS[b.key]}
                alt={b.name}
                className="w-full h-full object-contain p-3"
                style={{ transform: `scale(${b.logoScale || 1})`, transformOrigin: "center" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28 max-w-7xl mx-auto px-6 lg:px-8" data-testid={`branch-${b.key}-services`}>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Nos services</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Ce que nous faisons pour vous.
            </h2>
            <p className="mt-6 text-zinc-400">
              Un accompagnement sur-mesure, calibré selon votre activité, vos contraintes
              et vos objectifs.
            </p>
            <Link
              to={`/contact?branch=${b.key}`}
              data-testid={`branch-${b.key}-cta`}
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-md font-semibold transition-all border"
              style={{
                backgroundColor: b.accent,
                color: "#fff",
                borderColor: b.accent,
              }}
            >
              Demander un devis <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {b.services.filter((s) => s !== "Autre").map((svc, i) => (
              <div
                key={svc}
                className={`group rounded-lg border border-white/10 bg-[#0e0e0e] p-6 hover-lift hover:${b.accentBorder}`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: b.accentSoft, color: b.accent }}
                  >
                    <Check size={18} />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Service {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1 font-medium text-white">{svc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative py-24 border-t border-white/10 overflow-hidden"
        style={{ backgroundColor: "#070707" }}
        data-testid={`branch-${b.key}-cta-section`}
      >
        <div
          className="absolute inset-x-0 -top-20 h-72 blur-3xl opacity-30 pointer-events-none"
          style={{ background: `radial-gradient(closest-side, ${b.accent}, transparent)` }}
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            Un projet pour {b.name} ?
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
            Décrivez-nous votre besoin, nous revenons vers vous d'ici 24h !
          </p>
          <Link
            to={`/contact?branch=${b.key}`}
            data-testid={`branch-${b.key}-bottom-cta`}
            className="inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-md font-semibold"
            style={{ backgroundColor: b.accent, color: "#fff" }}
          >
            Démarrer ma demande <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
